import { useEffect, useState } from "react";
import { useTextos } from "../context/useTextos";
import { ITEM_STATUS, CHECKLIST_STATUS, getCategorias, ensureFotosGeraisItem, FOTOS_GERAIS_ITEM_ID } from "../lib/checklistTemplate";
import { useWorkshop } from "../context/useWorkshop";
import { carregarPessoas, filtrarPessoas, rotuloVeiculo, chavePlaca } from "../services/buscaUnificada";
import { salvarRascunho, lerRascunho, apagarRascunho, quandoFoi } from "../lib/rascunhoLocal";
import PhotoUpload from "./PhotoUpload";
import SignaturePad from "./SignaturePad";
import StatusBadge from "./StatusBadge";
import AvariasPanel from "./AvariasPanel";
import { normalizeAvarias } from "../lib/avariaModel";
import { VEHICLE_MODELS } from "../lib/vehicleModels";
import { useSettings } from "../context/useSettings";

// Fase 3 (biblioteca de veículos 3D): opções do seletor de tipo de
// veículo, geradas a partir do registro central -- adicionar um modelo
// novo no registro passa a aparecer aqui automaticamente, sem tocar neste
// arquivo. Modelos marcados "dev" já vêm com esse aviso no próprio texto
// (label), então aparecem no seletor de forma transparente.
const OPCOES_MODELO_3D = Object.entries(VEHICLE_MODELS).map(([id, m]) => ({ id, label: m.label }));

const STATUS_OPCOES = [
  { value: ITEM_STATUS.OK, i18nKey: "item_status_ok" },
  { value: ITEM_STATUS.ATENCAO, i18nKey: "item_status_atencao" },
  { value: ITEM_STATUS.CRITICO, i18nKey: "item_status_critico" },
  { value: ITEM_STATUS.NAO_APLICAVEL, i18nKey: "item_status_na" },
];

export default function ChecklistForm({ checklist, onSave, onCancel }) {
  const { t } = useTextos();
  const { workshopId } = useWorkshop();
  // AUD-A: escolher modelo de veículo 3D não faz sentido sem a inspeção 3D,
  // então o seletor acompanha a mesma chave (ver lib/settingsDefaults.js e
  // components/ChecklistConfigGate.js). O campo `veiculo.modelo3dId` continua
  // existindo no objeto -- nada é apagado de registro nenhum.
  const { settings } = useSettings();
  const inspecao3dLigada = !!(settings.features && settings.features.inspecao3d);
  const [data, setData] = useState(() => ({
    ...checklist,
    avarias: normalizeAvarias(checklist.avarias),
    itens: ensureFotosGeraisItem(checklist.itens),
  }));
  // AUD-B: categorias vindas dos itens DESTE checklist, nao do template
  // global -- ver getCategorias em lib/checklistTemplate.js.
  const categorias = getCategorias(data.itens);
  // Busca unificada: le os clientes do Checklist E os do painel de gestao,
  // que ate aqui eram duas listas separadas -- cada tela enxergava metade da
  // oficina. Nada e gravado nem migrado; ver services/buscaUnificada.js.
  const [pessoas, setPessoas] = useState([]);
  const [carregandoPessoas, setCarregandoPessoas] = useState(true);
  useEffect(() => {
    let cancelado = false;
    setCarregandoPessoas(true);
    carregarPessoas(workshopId)
      .then((lista) => {
        // Ignora resposta atrasada de uma oficina anterior.
        if (cancelado) return;
        setPessoas(lista);
      })
      .finally(() => {
        if (!cancelado) setCarregandoPessoas(false);
      });
    return () => { cancelado = true; };
  }, [workshopId]);
  const [pessoaSel, setPessoaSel] = useState(null);

  // ---- Rascunho local (ver lib/rascunhoLocal.js) -------------------------
  // Rede de proteção: enquanto o checklist é preenchido, uma cópia fica
  // gravada no próprio dispositivo. Se a aba fechar, o tablet dormir ou o
  // save for recusado, o trabalho é oferecido de volta na próxima abertura.
  // Nada aqui fala com o servidor, e a restauração é sempre uma escolha da
  // pessoa -- este código nunca sobrescreve o formulário sozinho.
  const [rascunhoAchado, setRascunhoAchado] = useState(null);
  const [decidiuRascunho, setDecidiuRascunho] = useState(false);

  useEffect(() => {
    let cancelado = false;
    lerRascunho(workshopId, checklist.id).then((r) => {
      if (cancelado || !r) { if (!cancelado) setDecidiuRascunho(true); return; }
      // Rascunho igual ao que já veio do servidor não tem o que restaurar.
      if (assinaturaDoConteudo(r.dados) === assinaturaDoConteudo(checklist)) {
        setDecidiuRascunho(true);
        return;
      }
      setRascunhoAchado(r);
    });
    return () => { cancelado = true; };
    // Lista de dependências vazia de propósito: isto roda uma vez, na
    // montagem. O id do checklist não muda enquanto o formulário vive, e
    // reexecutar aqui reabriria o aviso de restauração a cada digitada.
  }, []);

  useEffect(() => {
    // Enquanto o aviso de restauração está na tela, NÃO gravar: senão o
    // formulário em branco sobrescreveria justamente o rascunho oferecido.
    if (!decidiuRascunho) return;
    if (!temConteudo(data)) return;
    const timer = setTimeout(() => { salvarRascunho(workshopId, data); }, 900);
    return () => clearTimeout(timer);
  }, [data, decidiuRascunho, workshopId]);

  function restaurarRascunho() {
    if (!rascunhoAchado) return;
    setData({
      ...rascunhoAchado.dados,
      avarias: normalizeAvarias(rascunhoAchado.dados.avarias),
      itens: ensureFotosGeraisItem(rascunhoAchado.dados.itens),
    });
    setRascunhoAchado(null);
    setDecidiuRascunho(true);
  }

  function descartarRascunho() {
    apagarRascunho(workshopId, checklist.id);
    setRascunhoAchado(null);
    setDecidiuRascunho(true);
  }
  // ----------------------------------------------------------------------

  // Guarda qual placa FOI PREENCHIDA PELO SISTEMA. Serve para uma coisa só:
  // ao trocar de cliente, distinguir a placa que veio de uma seleção (do
  // cliente antigo, tem de sair) da placa que a pessoa digitou à mão (fica).
  const [placaAuto, setPlacaAuto] = useState("");
  const itensPendentes = data.itens.filter((i) => !i.status).length;
  const podeAprovar =
    itensPendentes === 0 && data.assinaturaCliente && data.assinaturaMecanico;
  const fotosGeraisItem = data.itens.find((i) => i.id === FOTOS_GERAIS_ITEM_ID);

  function updateField(path, value) {
    setData((prev) => {
      const next = structuredClone(prev);
      let obj = next;
      const keys = path.split(".");
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return next;
    });
  }

  function updateItem(itemId, patch) {
    setData((prev) => ({
      ...prev,
      itens: prev.itens.map((it) => (it.id === itemId ? { ...it, ...patch } : it)),
    }));
  }

  function handleSubmit(status) {
    onSave({ ...data, status });
  }

  function escolherVeiculo(v) {
    setPlacaAuto(chavePlaca(v && v.placa));
    setData((prev) => ({
      ...prev,
      // Só o id do Checklist referencia a tabela do Checklist. Veículo vindo
      // do painel preenche os campos e deixa veiculoId nulo, de propósito.
      veiculoId: v && v.origem === "checklist" ? v.id || null : null,
      veiculo: {
        ...prev.veiculo,
        placa: (v && v.placa) || "",
        marca: (v && v.marca) || "",
        modelo: (v && v.modelo) || "",
        ano: (v && v.ano) || "",
        km: (v && v.km) || "",
      },
    }));
  }

  function escolherPessoa(p) {
    // A placa que está no formulário foi preenchida pelo sistema numa seleção
    // anterior? Então é do cliente antigo e não pode continuar aí -- isto já
    // deixou placa trocada no formulário durante os testes. Se foi digitada à
    // mão, fica: ninguém apaga o que a pessoa escreveu.
    const atual = chavePlaca(data.veiculo.placa);
    const eraDoAnterior = !!atual && atual === placaAuto;
    if (eraDoAnterior) setPlacaAuto("");
    setPessoaSel(p);
    setData((prev) => ({
      ...prev,
      clienteId: p.checklistId || null,
      veiculoId: null,
      cliente: { ...prev.cliente, nome: p.nome, telefone: p.telefone || "" },
      veiculo: eraDoAnterior
        ? { ...prev.veiculo, placa: "", marca: "", modelo: "", ano: "", km: "" }
        : prev.veiculo,
    }));
    // Um veículo só: não faz sentido pedir para escolher. Mas só preenche se
    // o campo estiver livre -- vazio, ou com a placa que o próprio sistema
    // colocou antes. Placa digitada à mão nunca é sobrescrita.
    const campoLivre = !atual || eraDoAnterior;
    if (p.veiculos.length === 1 && campoLivre) escolherVeiculo(p.veiculos[0]);
  }

  function limparPessoa() {
    setPessoaSel(null);
    setData((prev) => ({ ...prev, clienteId: null, veiculoId: null }));
  }

  // "Não encontrei" -> começa um cadastro novo com o que a pessoa já digitou,
  // sem sair da tela e sem perder nada do que já foi preenchido no checklist.
  function comecarNovoCliente(nome) {
    setPessoaSel(null);
    setData((prev) => ({
      ...prev,
      clienteId: null,
      veiculoId: null,
      cliente: { ...prev.cliente, nome: nome || "" },
    }));
  }

  return (
    <div className="checklist-form">
      <header className="checklist-form__header">
        <h2>{t("checklist.form_titulo")}</h2>
        <StatusBadge status={data.status} kind="checklist" />
      </header>

      {rascunhoAchado && (
        <div style={ESTILO.rascunhoAviso}>
          <div style={{ minWidth: 0 }}>
            <span style={ESTILO.rascunhoTitulo}>
              {t("checklist.rascunho_encontrado", "Há um preenchimento não salvo deste checklist")}
            </span>
            <span style={ESTILO.rascunhoInfo}>
              {t("checklist.rascunho_quando", "Guardado neste aparelho")} {quandoFoi(rascunhoAchado.salvoEm)}
            </span>
          </div>
          <div style={ESTILO.rascunhoBotoes}>
            <button type="button" onClick={restaurarRascunho} style={ESTILO.botaoRestaurar}>
              {t("checklist.rascunho_restaurar", "Restaurar")}
            </button>
            <button type="button" onClick={descartarRascunho} style={ESTILO.botaoDescartar}>
              {t("checklist.rascunho_descartar", "Descartar")}
            </button>
          </div>
        </div>
      )}

      <section className="form-section">
        <h3>{t("checklist.form_dados_cliente")}</h3>
        <BuscaCadastro
          t={t}
          pessoas={pessoas}
          carregando={carregandoPessoas}
          selecionada={pessoaSel}
          onEscolher={escolherPessoa}
          onLimpar={limparPessoa}
          onNovoCliente={comecarNovoCliente}
        />
        <div className="form-grid">
          <label>
            {t("checklist.form_nome")}
            <input
              value={data.cliente.nome}
              onChange={(e) => updateField("cliente.nome", e.target.value)}
              placeholder={t("checklist.form_nome_placeholder")}
            />
          </label>
          <label>
            {t("checklist.form_telefone")}
            <input
              value={data.cliente.telefone}
              onChange={(e) => updateField("cliente.telefone", e.target.value)}
              placeholder={t("checklist.form_telefone_placeholder")}
            />
          </label>
        </div>
      </section>

      <section className="form-section">
        <h3>{t("checklist.form_dados_veiculo")}</h3>
        {pessoaSel && pessoaSel.veiculos.length > 0 && (
          <div style={ESTILO.veiculosBox}>
            <span style={ESTILO.veiculosRotulo}>
              {t("checklist.busca_veiculos_do_cliente", "Veículos deste cliente")}
            </span>
            <div style={ESTILO.veiculosLista}>
              {pessoaSel.veiculos.map((v, i) => {
                const ativo =
                  (data.veiculo.placa || "").toUpperCase() === (v.placa || "").toUpperCase() &&
                  !!v.placa;
                return (
                  <button
                    type="button"
                    key={(v.placa || "") + "-" + i}
                    onClick={() => escolherVeiculo(v)}
                    style={ativo ? ESTILO.chipVeiculoAtivo : ESTILO.chipVeiculo}
                  >
                    {rotuloVeiculo(v)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="form-grid">
          <label>
            {t("checklist.form_placa")}
            <input
              value={data.veiculo.placa}
              onChange={(e) => updateField("veiculo.placa", e.target.value.toUpperCase())}
              placeholder="ABC1D23"
            />
          </label>
          <label>
            {t("checklist.form_marca")}
            <input
              value={data.veiculo.marca}
              onChange={(e) => updateField("veiculo.marca", e.target.value)}
            />
          </label>
          <label>
            {t("checklist.form_modelo")}
            <input
              value={data.veiculo.modelo}
              onChange={(e) => updateField("veiculo.modelo", e.target.value)}
            />
          </label>
          <label>
            {t("checklist.form_ano")}
            <input
              value={data.veiculo.ano}
              onChange={(e) => updateField("veiculo.ano", e.target.value)}
              maxLength={4}
            />
          </label>
          <label>
            {t("checklist.form_km_atual")}
            <input
              value={data.veiculo.km}
              onChange={(e) => updateField("veiculo.km", e.target.value)}
            />
          </label>
          <label>
            {t("checklist.form_mecanico_responsavel")}
            <input
              value={data.mecanicoResponsavel}
              onChange={(e) => updateField("mecanicoResponsavel", e.target.value)}
            />
          </label>
          {inspecao3dLigada && (
            <label>
              {t("checklist.form_tipo_veiculo_3d")}
              <select
                value={data.veiculo.modelo3dId || ""}
                onChange={(e) => updateField("veiculo.modelo3dId", e.target.value)}
              >
                <option value="">{t("checklist.form_padrao_automatico")}</option>
                {OPCOES_MODELO_3D.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </section>

      {categorias.map((categoria) => (
        <section className="form-section" key={categoria}>
          <h3>{categoria}</h3>
          <div className="item-list">
            {data.itens
              .filter((item) => item.categoria === categoria)
              .map((item) => (
                <div className="item-row" key={item.id}>
                  <div className="item-row__top">
                    <span className="item-row__nome">{item.nome}</span>
                    <div className="item-row__status">
                      {STATUS_OPCOES.map((opt) => (
                        <button
                          type="button"
                          key={opt.value}
                          className={`status-btn status-btn--${opt.value} ${
                            item.status === opt.value ? "is-active" : ""
                          }`}
                          onClick={() => updateItem(item.id, { status: opt.value })}
                        >
                          {t(`checklist.${opt.i18nKey}`)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="item-row__bottom">
                    <input
                      className="item-row__obs"
                      placeholder={t("checklist.form_obs_item_placeholder")}
                      value={item.observacao}
                      onChange={(e) => updateItem(item.id, { observacao: e.target.value })}
                    />
                  </div>
                </div>
              ))}
          </div>
        </section>
      ))}

      <section className="form-section">
        <h3>{t("checklist.form_fotos_gerais_titulo")}</h3>
        <p className="hint">{t("checklist.form_fotos_gerais_hint")}</p>
        <PhotoUpload
          fotos={fotosGeraisItem?.fotos || []}
          onChange={(fotos) => updateItem(FOTOS_GERAIS_ITEM_ID, { fotos })}
          max={5}
        />
      </section>

      <AvariasPanel
        avarias={data.avarias}
        onChange={(avarias) => setData((prev) => ({ ...prev, avarias }))}
        modeloId={data.veiculo.modelo3dId || undefined}
      />

      <section className="form-section">
        <h3>{t("checklist.form_observacoes_gerais")}</h3>
        <textarea
          rows={3}
          value={data.observacoesGerais}
          onChange={(e) => updateField("observacoesGerais", e.target.value)}
          placeholder={t("checklist.form_observacoes_placeholder")}
        />
      </section>

      <section className="form-section form-section--signatures">
        <h3>{t("checklist.form_aprovacao")}</h3>
        <p className="hint">
          {itensPendentes > 0
            ? t("checklist.form_itens_pendentes", { count: itensPendentes })
            : t("checklist.form_todos_preenchidos")}
        </p>
        <div className="signatures-grid">
          <SignaturePad
            label={t("checklist.form_assinatura_mecanico")}
            value={data.assinaturaMecanico}
            onChange={(v) => updateField("assinaturaMecanico", v)}
          />
          <SignaturePad
            label={t("checklist.form_assinatura_cliente")}
            value={data.assinaturaCliente}
            onChange={(v) => updateField("assinaturaCliente", v)}
          />
        </div>
      </section>

      <footer className="checklist-form__footer">
        <button type="button" className="btn btn--ghost" onClick={onCancel}>
          {t("common.cancelar")}
        </button>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => handleSubmit(CHECKLIST_STATUS.RASCUNHO)}
        >
          {t("checklist.form_salvar_rascunho")}
        </button>
        <button
          type="button"
          className="btn btn--primary"
          disabled={!podeAprovar}
          onClick={() => handleSubmit(CHECKLIST_STATUS.APROVADO)}
          title={
            podeAprovar
              ? t("checklist.form_tooltip_pode_aprovar")
              : t("checklist.form_tooltip_nao_pode_aprovar")
          }
        >
          {t("checklist.form_concluir_aprovar")}
        </button>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Busca inteligente de cliente.
//
// Substitui o <select> que so enxergava a tabela do Checklist. Procura por
// nome, telefone, CPF, placa, marca ou modelo, nas duas bases ao mesmo tempo,
// sem acento e sem depender de maiusculas. Nao encontrou: oferece comecar o
// cadastro ali mesmo, com o que ja foi digitado.
//
// Estilo inline de proposito: nao encosta em checklist.css, que e compartilhado
// com o resto do modulo.
// ---------------------------------------------------------------------------
function BuscaCadastro({ t, pessoas, carregando, selecionada, onEscolher, onLimpar, onNovoCliente }) {
  const [termo, setTermo] = useState("");
  const [aberto, setAberto] = useState(false);

  const resultados = filtrarPessoas(pessoas, termo);
  const semResultado = !carregando && termo.trim().length >= 2 && resultados.length === 0;

  function escolher(p) {
    onEscolher(p);
    setTermo("");
    setAberto(false);
  }

  function cadastrar() {
    onNovoCliente(termo.trim());
    setTermo("");
    setAberto(false);
  }

  if (selecionada) {
    const origem =
      selecionada.origens.length > 1
        ? t("checklist.busca_origem_ambas", "Checklist + Painel")
        : selecionada.origens[0] === "painel"
        ? t("checklist.busca_origem_painel", "Painel")
        : t("checklist.busca_origem_checklist", "Checklist");
    return (
      <div style={ESTILO.selecionadoBox}>
        <div style={{ minWidth: 0 }}>
          <span style={ESTILO.selecionadoNome}>{selecionada.nome}</span>
          <span style={ESTILO.selecionadoInfo}>
            {selecionada.telefone || t("checklist.busca_sem_telefone", "sem telefone")}
            {" \u00b7 "}
            {origem}
          </span>
        </div>
        <button type="button" onClick={onLimpar} style={ESTILO.botaoTrocar}>
          {t("checklist.busca_trocar", "Trocar")}
        </button>
      </div>
    );
  }

  return (
    <div style={ESTILO.buscaWrap}>
      <label style={ESTILO.buscaLabel}>
        {t("checklist.busca_titulo", "Buscar cliente")}
        <input
          value={termo}
          onChange={(e) => {
            setTermo(e.target.value);
            setAberto(true);
          }}
          onFocus={() => setAberto(true)}
          placeholder={t("checklist.busca_placeholder", "Nome, telefone, CPF ou placa")}
          autoComplete="off"
        />
      </label>

      {carregando && (
        <span style={ESTILO.dica}>{t("checklist.busca_carregando", "Carregando cadastros...")}</span>
      )}

      {aberto && resultados.length > 0 && (
        <ul style={ESTILO.lista}>
          {resultados.map((p) => (
            <li key={p.chave}>
              <button type="button" onClick={() => escolher(p)} style={ESTILO.itemLista}>
                <span style={ESTILO.itemNome}>{p.nome}</span>
                <span style={ESTILO.itemInfo}>
                  {[
                    p.telefone,
                    p.veiculos.map((v) => v.placa).filter(Boolean).join(", "),
                  ]
                    .filter(Boolean)
                    .join(" \u00b7 ")}
                </span>
                <span style={ESTILO.itemTag}>
                  {p.origens.length > 1
                    ? t("checklist.busca_origem_ambas", "Checklist + Painel")
                    : p.origens[0] === "painel"
                    ? t("checklist.busca_origem_painel", "Painel")
                    : t("checklist.busca_origem_checklist", "Checklist")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {aberto && semResultado && (
        <div style={ESTILO.vazio}>
          <span style={ESTILO.vazioTexto}>
            {t("checklist.busca_nao_encontrado", "Nenhum cliente encontrado com este dado.")}
          </span>
          <button type="button" onClick={cadastrar} style={ESTILO.botaoCadastrar}>
            {t("checklist.busca_cadastrar", "Cadastrar como novo cliente")}
          </button>
          <span style={ESTILO.vazioDica}>
            {t(
              "checklist.busca_cadastrar_dica",
              "O nome entra nos campos abaixo e o cadastro completo pode ser feito depois, na tela de Clientes."
            )}
          </span>
        </div>
      )}
    </div>
  );
}

const ESTILO = {
  rascunhoAviso: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexWrap: "wrap", gap: 12, margin: "0 0 16px",
    padding: "12px 16px", borderRadius: 10,
    border: "1px solid rgba(217,157,38,.55)", background: "rgba(217,157,38,.10)",
  },
  rascunhoTitulo: { display: "block", fontWeight: 600, fontSize: 14 },
  rascunhoInfo: { display: "block", fontSize: 12.5, opacity: 0.78, marginTop: 2 },
  rascunhoBotoes: { display: "flex", gap: 8, flex: "0 0 auto" },
  botaoRestaurar: {
    font: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer",
    padding: "7px 14px", borderRadius: 8, border: "1px solid currentColor",
    background: "rgba(217,157,38,.18)", color: "inherit",
  },
  botaoDescartar: {
    font: "inherit", fontSize: 13, fontWeight: 600, cursor: "pointer",
    padding: "7px 14px", borderRadius: 8, border: "1px solid rgba(127,127,127,.45)",
    background: "transparent", color: "inherit",
  },
  buscaWrap: { position: "relative", marginBottom: 14 },
  buscaLabel: { display: "flex", flexDirection: "column", gap: 4 },
  dica: { display: "block", marginTop: 6, fontSize: 12, opacity: 0.7 },
  lista: {
    listStyle: "none",
    margin: "6px 0 0",
    padding: 4,
    border: "1px solid rgba(127,127,127,.35)",
    borderRadius: 10,
    background: "var(--surface, #fff)",
    maxHeight: 260,
    overflowY: "auto",
    boxShadow: "0 10px 30px -18px rgba(0,0,0,.6)",
  },
  itemLista: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gridTemplateAreas: '"nome tag" "info tag"',
    gap: "2px 10px",
    width: "100%",
    textAlign: "left",
    background: "transparent",
    border: "none",
    borderRadius: 7,
    cursor: "pointer",
    padding: "8px 10px",
    font: "inherit",
    color: "inherit",
  },
  itemNome: { gridArea: "nome", fontWeight: 600, fontSize: 14 },
  itemInfo: { gridArea: "info", fontSize: 12, opacity: 0.7 },
  itemTag: {
    gridArea: "tag",
    alignSelf: "center",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    opacity: 0.65,
    border: "1px solid rgba(127,127,127,.4)",
    borderRadius: 999,
    padding: "2px 8px",
    whiteSpace: "nowrap",
  },
  vazio: {
    marginTop: 6,
    padding: "12px 14px",
    border: "1px dashed rgba(127,127,127,.45)",
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 8,
  },
  vazioTexto: { fontSize: 13, fontWeight: 600 },
  vazioDica: { fontSize: 11.5, opacity: 0.7, lineHeight: 1.45 },
  botaoCadastrar: {
    font: "inherit",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    padding: "7px 13px",
    borderRadius: 8,
    border: "1px solid currentColor",
    background: "transparent",
    color: "inherit",
  },
  selecionadoBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 14,
    padding: "10px 14px",
    border: "1px solid rgba(127,127,127,.35)",
    borderRadius: 10,
  },
  selecionadoNome: { display: "block", fontWeight: 600, fontSize: 14 },
  selecionadoInfo: { display: "block", fontSize: 12, opacity: 0.7, marginTop: 2 },
  botaoTrocar: {
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: 8,
    border: "1px solid currentColor",
    background: "transparent",
    color: "inherit",
    flex: "0 0 auto",
  },
  veiculosBox: { marginBottom: 14 },
  veiculosRotulo: {
    display: "block",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    opacity: 0.65,
    marginBottom: 6,
  },
  veiculosLista: { display: "flex", flexWrap: "wrap", gap: 7 },
  chipVeiculo: {
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: 999,
    border: "1px solid rgba(127,127,127,.45)",
    background: "transparent",
    color: "inherit",
  },
  chipVeiculoAtivo: {
    font: "inherit",
    fontSize: 12.5,
    fontWeight: 700,
    cursor: "pointer",
    padding: "6px 12px",
    borderRadius: 999,
    border: "2px solid currentColor",
    background: "rgba(127,127,127,.14)",
    color: "inherit",
  },
};

// ---------------------------------------------------------------------------
// Apoio ao rascunho local.
// ---------------------------------------------------------------------------

// Vale a pena guardar? Um formulário recém-aberto e intocado não gera
// rascunho -- assim abrir e cancelar não deixa lixo acumulado no aparelho.
function temConteudo(c) {
  if (!c) return false;
  const cli = c.cliente || {};
  const vei = c.veiculo || {};
  if ((cli.nome || "").trim() || (cli.telefone || "").trim()) return true;
  if ((vei.placa || "").trim() || (vei.marca || "").trim() || (vei.modelo || "").trim()) return true;
  if ((vei.ano || "").trim() || String(vei.km || "").trim()) return true;
  if ((c.mecanicoResponsavel || "").trim()) return true;
  if ((c.observacoesGerais || "").trim()) return true;
  if (c.assinaturaCliente || c.assinaturaMecanico) return true;
  const itens = Array.isArray(c.itens) ? c.itens : [];
  // O item "fotos gerais" é injetado por ensureFotosGeraisItem() já com
  // status preenchido -- é andaime da estrutura, não algo que a pessoa fez.
  // Contar o status dele faria todo formulário recém-aberto gerar rascunho.
  // As fotos dele, essas sim, contam.
  if (itens.some((i) => {
    const andaime = i.id === FOTOS_GERAIS_ITEM_ID;
    if (!andaime && i.status) return true;
    if ((i.observacao || "").trim()) return true;
    if ((i.fotos || []).length) return true;
    return false;
  })) return true;
  const av = c.avarias || {};
  if ((av.diagrama || []).length || (av.fotos || []).length) return true;
  return false;
}

// Assinatura barata do conteúdo, só para responder "este rascunho é
// diferente do que o servidor mandou?". Não percorre base64 de foto: usa a
// contagem, que é o suficiente e não custa nada numa tela de 1,5 MB.
function assinaturaDoConteudo(c) {
  if (!c) return "";
  const cli = c.cliente || {};
  const vei = c.veiculo || {};
  const itens = Array.isArray(c.itens) ? c.itens : [];
  const av = c.avarias || {};
  return [
    c.status || "",
    cli.nome || "", cli.telefone || "",
    vei.placa || "", vei.marca || "", vei.modelo || "", vei.ano || "", vei.km || "",
    c.mecanicoResponsavel || "",
    (c.observacoesGerais || "").length,
    c.assinaturaCliente ? "ac" : "", c.assinaturaMecanico ? "am" : "",
    itens.map((i) => (i.status || "-") + ":" + (i.observacao || "").length + ":" + ((i.fotos || []).length)).join("|"),
    "d" + ((av.diagrama || []).length), "f" + ((av.fotos || []).length),
  ].join("~");
}
